import React, { useState, useEffect } from 'react'
import { navigate, Link } from 'gatsby'
import Form, { useForm } from 'react-dynamic-form'
import styled, { css } from 'styled-components'
import { useLocation, useMount, useHash } from 'react-use';
import OutsideClickHandler from 'react-outside-click-handler';

import { Field } from '..'
import { addToMailchimp } from '../../services/mailchimp'

import { isClient, media } from '../../styles/utils'
import { validators } from '../../validators'
import { container, padding, type, textHover, colours } from '../../styles/global'
import { getGlobalData } from '../../utils'

// Client-side only wrapper component
const ClientOnly = ({ children }) => {
	const [hasMounted, setHasMounted] = useState(false);
	
	useEffect(() => {
		setHasMounted(true);
	}, []);
	
	// Render nothing on the server
	if (!hasMounted) {
		return null;
	}
	
	// Render children on the client
	return children;
};

// The actual Newsletter component implementation
// This will only be rendered on the client side
const NewsletterImplementation = (props) => {
	const { data } = getGlobalData('subscribe_modal')
	const [active, setActive] = useState(false)
	const [form, { fieldState, formSubmit, setError }] = useForm()
	const [formComplete, setFormComplete] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const location = useLocation()
	const [hash, setHash] = useHash() // Safe to use now, only runs on client

	useEffect(() => {
		// Safe to use browser APIs directly now
		window.showNewsletter = show;
		
		// Only run auto-show logic on initial mount for homepage
		if (location?.pathname === '/' && localStorage.announcementSeen !== 'true') {
			const timer = setTimeout(() => setActive(true), 5000);
			return () => clearTimeout(timer);
		}
	}, [location?.pathname]); // Only depend on pathname

	useEffect(() => {
		// Handle hash-based showing separately
		if (window.location.hash === '#showNewsletter') {
			show();
			setHash('newsletter');
		}
		
		// Check for Instagram traffic
		checkInstagramTraffic();
	}, [hash]); // Separate effect for hash changes

	const checkInstagramTraffic = () => {
		// No isClient check needed anymore
		try {
			const searchParams = new URLSearchParams(window.location.search)
			const isFromInstagram = searchParams.get('utm_source') === 'later-linkinbio'

			if (isFromInstagram && !active) {
				show()
			}
		} catch (e) {
			console.error('Error checking Instagram traffic:', e);
		}
	}

	const hide = () => {
		localStorage.announcementSeen = true
		setActive(false)
	}

	const handleOutsideClick = (e) => {
		if (e.target.classList.contains('close-button') || e.target.closest('.close-button')) {
			return;
		}
		hide();
	}

	const handleCloseClick = (e) => {
		e.preventDefault();
		e.stopPropagation();
		hide();
	}

	const show = () => {
		setActive(true)
	}

	const handleSubmit = (fields, reset) => {
		setIsSubmitting(true) // Start loading state
		
		let data = {
			email: fields['email'].value,
		}

		addToMailchimp(data.email)
			.then(mc_resp => {
				if (mc_resp.result == 'success') {
					localStorage.announcementSeen = true
					setFormComplete(true)
					setError(false)
				}

				if (mc_resp.result == 'error') {
					setError(mc_resp.msg)
				}
			})
			.catch(error => {
				// In development, just show success to test the UI
				if (process.env.NODE_ENV === 'development') {
					localStorage.announcementSeen = true
					setFormComplete(true)
					setError(false)
				} else {
					setError('Subscription failed. Please try again.')
				}
			})
			.finally(() => {
				setIsSubmitting(false) // End loading state
			})

		reset()
	}

	const renderSubscribe = () => {
		const fields = [
			{
				type: 'email',
				name: 'email',
				label: 'E-MAIL:',
				placeholder: ' ',
				validator: validators.email,
				errorMessage: 'Please enter a valid email'
			},
		]

		return (
			<Subscribe>
				{!formComplete && (
					<>
						<Form
							form={form}
							data={fields}
							styles={formStyles}
							renderInput={<Field />}
							renderSubmit={false}
							onSubmit={handleSubmit}
							moveToInvalidField={false}
						/>

						<Submit
							onClick={formSubmit}
							disabled={isSubmitting}
						>
							{isSubmitting ? 'Subscribing...' : 'Subscribe'}
						</Submit>

						{form.error && <Error>{form.error}</Error>}
					</>
				)}

				{formComplete && (
					<Subheading>
						Subscribed
					</Subheading>
				)}
			</Subscribe>
		)
	}

	return (
		<OutsideClickHandler
			onOutsideClick={handleOutsideClick}
		>
			<Wrapper
				active={active}
			>
				{data?.description && (
					<Text
						dangerouslySetInnerHTML={{__html: data.description}}
					/>
				)}

				{renderSubscribe()}

				<Heading>
					Mail
				</Heading>

				<CloseButton
					className="close-button"
					onClick={handleCloseClick}
				>
					Close
				</CloseButton>
			</Wrapper>
		</OutsideClickHandler>
	)
}

// Actual exported component is the client-only wrapper
const Newsletter = (props) => (
	<ClientOnly>
		<NewsletterImplementation {...props} />
	</ClientOnly>
);

// Shared

const Heading = styled.div``
const Subheading = styled.div``
const Text = styled.div``
const Info = styled.div``
const Item = styled.div``

// Layout

const Wrapper = styled.div`
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: 10;
	background: ${colours.offWhite};
	max-width: 510px;
	padding: 40px 65px 16px;

	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;

	${media.phone`
		max-width: calc(100vw - 48px);
		padding: 20px 20px 16px;
		width: 100%;
	`}

	${Text} {
		&, p {
			${type.bodySmall};	
			text-align: center;
		}
	}

	${Heading} {
		margin: 190px 0 180px;
		font-size: 80px;
		letter-spacing: 0.04em;
		text-transform: uppercase;
		text-align: center;	
		line-height: 0;

		${media.phone`
			font-size: 60px;
			margin: 90px 0 80px;
		`}
	}

	${Subheading} {
		${type.caption};
		cursor: pointer;
		padding: 12px;
		transition: opacity 0.25s ease;
		
		&:hover {
			opacity: 0.7;
		}
		
		&.close-button {
			background: none;
			border: none;
			outline: none;
			user-select: none;
			-webkit-tap-highlight-color: transparent;
		}
	}

	/* Active */

	transition: opacity 0.5s ease;
	opacity: 0;
	will-change: opacity;
	pointer-events: none;
	
	${props => {
		if (props.active) return css`
			opacity: 1;
			pointer-events: all;
		`
		else return css`
			opacity: 0 !important;
			pointer-events: none !important;
			visibility: hidden;
		`
	}}
`

// Subscribe	

const Submit = styled.div`
	${type.bodySmall};	
	margin-top: 42px;
	text-transform: uppercase;
	cursor: pointer;
	transition: opacity 0.25s ease;

	${media.phone`
		margin-top: 24px;
	`}
	
	&[disabled] {
		opacity: 0.6;
		cursor: not-allowed;
		pointer-events: none;
	}
`

const Subscribe = styled.div`
	width: 100%;

	${Subheading} {
		text-transform: uppercase;
		margin-top: 42px;
		${type.bodySmall};

		${media.phone`
			margin-top: 24px;
		`}
	}
`

const formStyles = css`
    width: 100%;
	margin-top: 20px;

    .dynamic-fields {
        display: flex;
        flex-flow: row wrap;
        justify-content: space-between;
        width: 100%;

        .dynamic-field {
			&, input {
				height: 1.5em !important;
				
				&, 
				&::placeholder,
				.field-label {
					${type.bodySmall}
				}

				.field-label {
					min-width: 70px;
					margin-right: 2px;
				}
			}

			input {
				border-bottom: 1px solid black;
			}
        }
    }
`

export const Error = styled.div`
    color: ${colours.red};
    margin-top: 16px;
`

const CloseButton = styled.button`
	${type.caption};
	cursor: pointer;
	padding: 12px;
	transition: opacity 0.25s ease;
	background: none;
	border: none;
	outline: none;
	user-select: none;
	-webkit-tap-highlight-color: transparent;
	
	&:hover {
		opacity: 0.7;
	}
	
	&:active {
		opacity: 0.5;
	}
`

export default Newsletter